<template>
  <div
    v-if="shouldShowAds"
    :class="['adsense-container', containerClass]"
    :style="containerStyle"
  >
    <ins
      class="adsbygoogle"
      :style="adStyle"
      :data-ad-client="adClient"
      :data-ad-slot="adSlot"
      :data-ad-format="adFormat"
      :data-full-width-responsive="fullWidthResponsive"
      :data-ad-layout="adLayout"
      :data-ad-layout-key="adLayoutKey"
    />
  </div>
</template>

<script>
import adsenseService from '@/services/adsenseService.js';

export default {
  name: 'AdSenseUnit',
  props: {
    // Required props
    adSlot: {
      type: String,
      required: true,
      validator: (value) => value.length > 0,
    },

    // Ad format (auto, rectangle, horizontal, vertical, etc.)
    adFormat: {
      type: String,
      default: 'auto',
    },

    // Ad layout for in-feed or in-article ads
    adLayout: {
      type: String,
      default: null,
    },

    // Ad layout key for in-feed ads
    adLayoutKey: {
      type: String,
      default: null,
    },

    // Responsive behavior
    fullWidthResponsive: {
      type: [Boolean, String],
      default: true,
    },

    // Custom styling
    width: {
      type: [String, Number],
      default: null,
    },

    height: {
      type: [String, Number],
      default: null,
    },

    // Container classes
    containerClass: {
      type: [String, Array, Object],
      default: '',
    },

    // Test mode
    testMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      adClient: 'ca-pub-8326584585992829',
      initialized: false,
    };
  },

  computed: {
    shouldShowAds() {
      return this.testMode || adsenseService.shouldShowAds();
    },

    adStyle() {
      const style = {
        display: 'block',
        width: '100%',
      };

      if (this.width) {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
      }

      if (this.height) {
        style.height = typeof this.height === 'number' ? `${this.height}px` : this.height;
      }

      return style;
    },

    containerStyle() {
      return {
        textAlign: 'center',
        margin: '20px 0',
        width: '100%',
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initializeAd();
    });
  },

  beforeUnmount() {
    if (this.initialized) {
      adsenseService.removeAdUnit(this.adSlot);
    }
  },

  methods: {
    initializeAd() {
      if (!this.shouldShowAds) {
        return;
      }

      // Wait for AdSense to be available
      const tryInitialize = () => {
        if (adsenseService.isAvailable()) {
          const adElement = this.$el.querySelector('.adsbygoogle');
          if (adElement && !adElement.getAttribute('data-adsbygoogle-status')) {
            this.initialized = adsenseService.pushAd(this.adSlot, adElement);
          }
        } else {
          setTimeout(tryInitialize, 100);
        }
      };

      tryInitialize();
    },

    refreshAd() {
      if (this.initialized) {
        adsenseService.removeAdUnit(this.adSlot);
        this.$nextTick(() => {
          this.initializeAd();
        });
      }
    },
  },
};
</script>

<style scoped>
.adsense-container {
  overflow: hidden;
  margin: 20px 0;
}

.adsense-container.banner {
  text-align: center;
}

.adsense-container.sidebar {
  margin: 10px 0;
}

.adsense-container.in-article {
  margin: 30px 0;
}

.adsense-container.in-feed {
  margin: 15px 0;
}

@media (max-width: 768px) {
  .adsense-container {
    margin: 15px 0;
  }
}
</style>
